import { firestore } from '../database/firebase';
import {
  collection,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  getDocs,
  query,
  where,
  orderBy,
  serverTimestamp,
  addDoc,
} from 'firebase/firestore';
import { resolveUserRolesByEmailOrUid, normalizeRoleName } from '../utils/roleResolver';

export function canViewAll(userRole) {
  const normalized = normalizeRoleName(userRole);
  return ['SuperAdmin', 'Admin', 'Moderator', 'Coach', 'Business Advisor'].includes(normalized);
}

export function canCreate(userRole) {
  const normalized = normalizeRoleName(userRole);
  return ['SuperAdmin', 'Admin', 'Client', 'Moderator', 'Coach', 'Business Advisor'].includes(
    normalized
  );
}

export function canEdit(userRole, userId, ownerId) {
  const normalized = normalizeRoleName(userRole);
  // SuperAdmin, Admin, Business Advisor, Moderator, Coach can edit all
  if (['SuperAdmin', 'Admin', 'Moderator', 'Coach', 'Business Advisor'].includes(normalized))
    return true;
  // Clients can only edit their own
  if (normalized === 'Client' && userId === ownerId) return true;
  return false;
}

export function canDelete(userRole, userId, ownerId) {
  const normalized = normalizeRoleName(userRole);
  // SuperAdmin, Admin, Business Advisor, Moderator, Coach can delete all
  if (['SuperAdmin', 'Admin', 'Moderator', 'Coach', 'Business Advisor'].includes(normalized))
    return true;
  // Clients can only delete their own
  if (normalized === 'Client' && userId === ownerId) return true;
  return false;
}

export function canApprove(userRole) {
  const normalized = normalizeRoleName(userRole);
  return ['SuperAdmin', 'Admin', 'Moderator', 'Business Advisor'].includes(normalized);
}

export function canComment(userRole) {
  const normalized = normalizeRoleName(userRole);
  return ['SuperAdmin', 'Admin', 'Moderator', 'Coach', 'Business Advisor'].includes(normalized);
}

// Readiness Checklist CRUD
export async function getReadinessChecklist(userId, currentUser) {
  if (!currentUser?.uid) throw new Error('User not authenticated');

  const roles = await resolveUserRolesByEmailOrUid({
    email: currentUser.email,
    uid: currentUser.uid,
  });

  // Clients can only view their own
  if (normalizeRoleName(roles.highestRole) === 'Client' && userId !== currentUser.uid) {
    throw new Error('Access denied');
  }

  const docRef = doc(firestore, 'commercialisation', userId, 'readinessChecklist', userId);
  const docSnap = await getDoc(docRef);

  if (docSnap.exists()) {
    return { id: docSnap.id, ...docSnap.data() };
  }
  return null;
}

export async function saveReadinessChecklist(userId, data, currentUser) {
  if (!currentUser?.uid) throw new Error('User not authenticated');

  const roles = await resolveUserRolesByEmailOrUid({
    email: currentUser.email,
    uid: currentUser.uid,
  });

  // Clients can only save their own
  if (normalizeRoleName(roles.highestRole) === 'Client' && userId !== currentUser.uid) {
    throw new Error('Access denied');
  }

  const docRef = doc(firestore, 'commercialisation', userId, 'readinessChecklist', userId);
  const parentRef = doc(firestore, 'commercialisation', userId);
  const timestamp = serverTimestamp();

  await setDoc(
    parentRef,
    {
      userId,
      userEmail: currentUser.email,
      lastUpdatedAt: timestamp,
      modules: {
        readinessChecklist: {
          lastUpdatedAt: timestamp,
          score: data?.score ?? null,
          readinessLevel: data?.readinessLevel ?? null,
        },
      },
    },
    { merge: true }
  );

  await setDoc(
    docRef,
    {
      ...data,
      userId,
      userEmail: currentUser.email,
      updatedAt: serverTimestamp(),
      createdAt: data.createdAt || serverTimestamp(),
    },
    { merge: true }
  );

  return docRef.id;
}

export async function deleteReadinessChecklist(userId, currentUser) {
  if (!currentUser?.uid) throw new Error('User not authenticated');

  const roles = await resolveUserRolesByEmailOrUid({
    email: currentUser.email,
    uid: currentUser.uid,
  });

  // Only SuperAdmin, Admin or owner can delete
  if (normalizeRoleName(roles.highestRole) !== 'Admin' && userId !== currentUser.uid) {
    throw new Error('Access denied');
  }

  const docRef = doc(firestore, 'commercialisation', userId, 'readinessChecklist', userId);
  await deleteDoc(docRef);
}

// GTM Plan CRUD
export async function getGTMPlan(userId, currentUser) {
  if (!currentUser?.uid) throw new Error('User not authenticated');

  const roles = await resolveUserRolesByEmailOrUid({
    email: currentUser.email,
    uid: currentUser.uid,
  });

  if (normalizeRoleName(roles.highestRole) === 'Client' && userId !== currentUser.uid) {
    throw new Error('Access denied');
  }

  const docRef = doc(firestore, 'commercialisation', userId, 'gtmPlan', userId);
  const docSnap = await getDoc(docRef);

  if (docSnap.exists()) {
    return { id: docSnap.id, ...docSnap.data() };
  }
  return null;
}

export async function saveGTMPlan(userId, data, currentUser) {
  if (!currentUser?.uid) throw new Error('User not authenticated');

  const roles = await resolveUserRolesByEmailOrUid({
    email: currentUser.email,
    uid: currentUser.uid,
  });

  if (normalizeRoleName(roles.highestRole) === 'Client' && userId !== currentUser.uid) {
    throw new Error('Access denied');
  }

  const docRef = doc(firestore, 'commercialisation', userId, 'gtmPlan', userId);
  const parentRef = doc(firestore, 'commercialisation', userId);
  const timestamp = serverTimestamp();

  await setDoc(
    parentRef,
    {
      userId,
      userEmail: currentUser.email,
      lastUpdatedAt: timestamp,
      modules: {
        gtmPlan: {
          lastUpdatedAt: timestamp,
          progress: data?.progress ?? null,
          currentStep: data?.currentStep ?? null,
          isDraft: data?.isDraft ?? null,
        },
      },
    },
    { merge: true }
  );

  await setDoc(
    docRef,
    {
      ...data,
      userId,
      userEmail: currentUser.email,
      updatedAt: serverTimestamp(),
      createdAt: data.createdAt || serverTimestamp(),
    },
    { merge: true }
  );

  return docRef.id;
}

export async function deleteGTMPlan(userId, currentUser) {
  if (!currentUser?.uid) throw new Error('User not authenticated');

  const roles = await resolveUserRolesByEmailOrUid({
    email: currentUser.email,
    uid: currentUser.uid,
  });

  if (
    normalizeRoleName(roles.highestRole) !== 'SuperAdmin' &&
    normalizeRoleName(roles.highestRole) !== 'Admin' &&
    userId !== currentUser.uid
  ) {
    throw new Error('Access denied');
  }

  const docRef = doc(firestore, 'commercialisation', userId, 'gtmPlan', userId);
  await deleteDoc(docRef);
}

// Launch Checklist CRUD
export async function getLaunchChecklist(userId, currentUser) {
  if (!currentUser?.uid) throw new Error('User not authenticated');

  const roles = await resolveUserRolesByEmailOrUid({
    email: currentUser.email,
    uid: currentUser.uid,
  });

  if (normalizeRoleName(roles.highestRole) === 'Client' && userId !== currentUser.uid) {
    throw new Error('Access denied');
  }

  const docRef = doc(firestore, 'commercialisation', userId, 'launchChecklist', userId);
  const docSnap = await getDoc(docRef);

  if (docSnap.exists()) {
    return { id: docSnap.id, ...docSnap.data() };
  }
  return null;
}

export async function saveLaunchChecklist(userId, data, currentUser) {
  if (!currentUser?.uid) throw new Error('User not authenticated');

  const roles = await resolveUserRolesByEmailOrUid({
    email: currentUser.email,
    uid: currentUser.uid,
  });

  if (normalizeRoleName(roles.highestRole) === 'Client' && userId !== currentUser.uid) {
    throw new Error('Access denied');
  }

  const docRef = doc(firestore, 'commercialisation', userId, 'launchChecklist', userId);
  const parentRef = doc(firestore, 'commercialisation', userId);
  const timestamp = serverTimestamp();

  await setDoc(
    parentRef,
    {
      userId,
      userEmail: currentUser.email,
      lastUpdatedAt: timestamp,
      modules: {
        launchChecklist: {
          lastUpdatedAt: timestamp,
          score: data?.score ?? null,
          readinessLevel: data?.readinessLevel ?? null,
        },
      },
    },
    { merge: true }
  );

  await setDoc(
    docRef,
    {
      ...data,
      userId,
      userEmail: currentUser.email,
      updatedAt: serverTimestamp(),
      createdAt: data.createdAt || serverTimestamp(),
    },
    { merge: true }
  );

  return docRef.id;
}

export async function deleteLaunchChecklist(userId, currentUser) {
  if (!currentUser?.uid) throw new Error('User not authenticated');

  const roles = await resolveUserRolesByEmailOrUid({
    email: currentUser.email,
    uid: currentUser.uid,
  });

  if (
    normalizeRoleName(roles.highestRole) !== 'SuperAdmin' &&
    normalizeRoleName(roles.highestRole) !== 'Admin' &&
    userId !== currentUser.uid
  ) {
    throw new Error('Access denied');
  }

  const docRef = doc(firestore, 'commercialisation', userId, 'launchChecklist', userId);
  await deleteDoc(docRef);
}

// Get all submissions (Admin/Moderator/Coach/Business Advisor only)
export async function getAllSubmissions(currentUser) {
  if (!currentUser?.uid) throw new Error('User not authenticated');

  const roles = await resolveUserRolesByEmailOrUid({
    email: currentUser.email,
    uid: currentUser.uid,
  });

  if (!canViewAll(roles.highestRole)) {
    throw new Error('Access denied');
  }

  const submissions = [];
  const commercialisationRef = collection(firestore, 'commercialisation');
  const usersSnapshot = await getDocs(commercialisationRef);

  const userDocMap = new Map();
  usersSnapshot.forEach(docSnap => {
    userDocMap.set(docSnap.id, docSnap.data() || {});
  });

  const clientsSnapshot = await getDocs(collection(firestore, 'clients'));
  const clientsMap = {};
  clientsSnapshot.forEach(docSnap => {
    clientsMap[docSnap.id] = {
      email: docSnap.data()?.email || '',
      name:
        `${docSnap.data()?.firstname || ''} ${docSnap.data()?.lastname || ''}`.trim() ||
        docSnap.data()?.email ||
        'Unknown',
    };
  });

  const candidateIds = new Set([...userDocMap.keys(), ...Object.keys(clientsMap)]);

  // Process all users in parallel instead of sequentially
  const userPromises = Array.from(candidateIds).map(async userId => {
    const metadata = userDocMap.get(userId) || {};
    const clientInfo = clientsMap[userId];

    const userData = {
      userId,
      ...metadata,
    };

    if (clientInfo) {
      userData.clientName = clientInfo.name;
      if (!userData.userEmail) {
        userData.userEmail = clientInfo.email;
      }
    }

    // Fetch all three checklists in parallel
    const [readinessSnap, gtmSnap, launchSnap] = await Promise.all([
      getDoc(doc(firestore, 'commercialisation', userId, 'readinessChecklist', userId)),
      getDoc(doc(firestore, 'commercialisation', userId, 'gtmPlan', userId)),
      getDoc(doc(firestore, 'commercialisation', userId, 'launchChecklist', userId)),
    ]);

    let hasData = false;

    if (readinessSnap.exists()) {
      userData.readinessChecklist = { id: readinessSnap.id, ...readinessSnap.data() };
      hasData = true;
    }

    if (gtmSnap.exists()) {
      userData.gtmPlan = { id: gtmSnap.id, ...gtmSnap.data() };
      hasData = true;
    }

    if (launchSnap.exists()) {
      userData.launchChecklist = { id: launchSnap.id, ...launchSnap.data() };
      hasData = true;
    }

    return hasData ? userData : null;
  });

  const results = await Promise.all(userPromises);
  return results.filter(Boolean);
}

// Approve/Flag submissions
export async function updateReadinessStatus(userId, status, comments, currentUser) {
  if (!currentUser?.uid) throw new Error('User not authenticated');

  const roles = await resolveUserRolesByEmailOrUid({
    email: currentUser.email,
    uid: currentUser.uid,
  });

  if (!canApprove(roles.highestRole)) {
    throw new Error('Access denied');
  }

  const docRef = doc(firestore, 'commercialisation', userId, 'readinessChecklist', userId);
  await updateDoc(docRef, {
    status,
    approvedBy: currentUser.uid,
    approvedByEmail: currentUser.email,
    approvedAt: serverTimestamp(),
    comments,
  });
}

// Comments/Feedback
export async function addComment(userId, checklistType, comment, currentUser) {
  if (!currentUser?.uid) throw new Error('User not authenticated');

  const roles = await resolveUserRolesByEmailOrUid({
    email: currentUser.email,
    uid: currentUser.uid,
  });

  if (!canComment(roles.highestRole)) {
    throw new Error('Access denied');
  }

  const commentsRef = collection(
    firestore,
    'commercialisation',
    userId,
    'readinessChecklist',
    userId,
    'comments'
  );

  await addDoc(commentsRef, {
    comment,
    commentedBy: currentUser.uid,
    commentedByEmail: currentUser.email,
    commentedByName: currentUser.displayName || currentUser.email,
    createdAt: serverTimestamp(),
    checklistType,
  });
}

export async function getComments(userId, checklistType, currentUser) {
  if (!currentUser?.uid) throw new Error('User not authenticated');

  const roles = await resolveUserRolesByEmailOrUid({
    email: currentUser.email,
    uid: currentUser.uid,
  });

  // Clients can view their own comments, privileged users can view all
  if (normalizeRoleName(roles.highestRole) === 'Client' && userId !== currentUser.uid) {
    throw new Error('Access denied');
  }

  const commentsRef = collection(
    firestore,
    'commercialisation',
    userId,
    'readinessChecklist',
    userId,
    'comments'
  );

  const q = query(
    commentsRef,
    where('checklistType', '==', checklistType),
    orderBy('createdAt', 'desc')
  );
  const snapshot = await getDocs(q);

  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data(),
  }));
}
